import type { ExtractResponseType } from "@hyper-fetch/core";
import { useFetch } from "@hyper-fetch/react";
import { Link } from "@tanstack/react-router";
import { MapPin, SearchX, Video } from "lucide-react";
import { useEffect, useState } from "react";

import { ResultCard } from "./components/result-card";
import type { CardPick, CardTimes, DirectoryCard } from "./components/result-card";
import { sdk } from "@/api/sdk";
import { GROUP_ICON } from "@/features/directory/modules/search-bar/search-bar";
import { cardKey } from "@/features/directory/utils/card-requests";
import { apiQuery } from "@/features/directory/utils/search-params";
import type { DirectorySearch, DirectorySort } from "@/features/directory/utils/search-params";
import { searchPagePath } from "@/features/directory/utils/search-path";
import { SERVICE_TYPE_COPY } from "@/features/directory/utils/service-type-copy";
import { SlotGateDialog } from "@/features/site/modules/open-calendar/slot-gate-dialog";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { foldSearchText, SERVICE_TYPE_GROUP, SERVICE_TYPES } from "@complete-athlete/enums";
import type { ServiceType } from "@complete-athlete/enums";

export type DirectoryResult = ExtractResponseType<typeof sdk.directory.search.$get>;
export type DirectoryCity = ExtractResponseType<typeof sdk.directory.cities.$get>[number];

const chip =
  "inline-flex h-9 items-center gap-1.5 rounded-full border border-border bg-background px-3.5 text-[13px] font-medium whitespace-nowrap transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none";

/** The other types on the same shelf: what someone looking for this might want instead. */
const relatedTypes = (service: ServiceType) =>
  SERVICE_TYPES.filter((type) => type !== service && SERVICE_TYPE_GROUP[type] === SERVICE_TYPE_GROUP[service]).slice(
    0,
    8,
  );

/**
 * The answer to a search: how many, in what order, the cards, and where to go next. The first page
 * is the route's (read on the server, so the page arrives filled and search engines see it); more
 * pages join it below, on request. Every way onward is a link to another search, so it can be
 * opened in a new tab, shared, and crawled.
 */
export function SearchResults({
  search,
  first,
  firstTimes,
  cities,
  onChange,
}: {
  search: DirectorySearch & { sort?: DirectorySort };
  first: DirectoryResult | null;
  /** The first cards' open times, read with the page, by `cardKey`. */
  firstTimes: Record<string, CardTimes | null>;
  cities: DirectoryCity[];
  onChange: (next: DirectorySearch & { sort?: DirectorySort }) => void;
}) {
  const [page, setPage] = useState(1);
  const [more, setMore] = useState<DirectoryCard[]>([]);
  const [pick, setPick] = useState<CardPick | null>(null);
  const next = useFetch(sdk.directory.search.$get.setQueryParams(apiQuery(search, page)), {
    disabled: page === 1,
    dependencies: [page],
  });
  // Each page that arrives joins the list once. The route mounts a fresh list for a new search.
  useEffect(() => {
    const arrived = next.data;
    if (!arrived || arrived.page === 1) return;
    setMore((kept) => [
      ...kept,
      ...arrived.cards.filter(
        (card) =>
          !kept.some((old) => old.organizationId === card.organizationId && old.specialist.id === card.specialist.id),
      ),
    ]);
  }, [next.data]);

  const cards = [...(first?.cards ?? []), ...more];
  const total = first?.total ?? 0;
  const sort = first?.sort ?? search.sort ?? "relevance";
  const sorts: DirectorySort[] = search.near ? ["distance", "relevance", "price"] : ["relevance", "price"];
  const otherCities = cities.filter((entry) => foldSearchText(entry.city) !== foldSearchText(search.city ?? ""));

  return (
    <div className="flex flex-col gap-4 pt-4">
      <div className="flex flex-wrap items-center justify-between gap-3 px-1">
        <p className="text-sm text-muted-foreground tabular-nums">{m.directory_results({ count: total })}</p>
        <div className="flex max-w-full flex-wrap items-center gap-2">
          <div
            role="radiogroup"
            aria-label={m.directory_sort_label()}
            className="inline-flex w-fit max-w-full overflow-x-auto rounded-full border border-border bg-background p-1"
          >
            {sorts.map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={sort === option}
                onClick={() => onChange({ ...search, sort: option })}
                className={cn(
                  "h-8 rounded-full px-3.5 text-[13px] font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                  sort === option ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {option === "distance"
                  ? m.directory_sort_distance()
                  : option === "price"
                    ? m.directory_sort_price()
                    : m.directory_sort_relevance()}
              </button>
            ))}
          </div>
          <button
            type="button"
            aria-pressed={Boolean(search.online)}
            onClick={() => onChange({ ...search, online: search.online ? undefined : true })}
            className={cn(chip, "h-10", search.online && "border-primary bg-primary/12 text-foreground")}
          >
            <Video aria-hidden className="size-4" />
            {m.directory_filter_online()}
          </button>
        </div>
      </div>

      {cards.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {cards.map((card) => (
            <li key={cardKey(card)}>
              <ResultCard card={card} initialTimes={firstTimes[cardKey(card)]} onPick={setPick} />
            </li>
          ))}
        </ul>
      ) : (
        <div className="grid gap-6 rounded-[32px] bg-[var(--site-tint)] p-6 md:p-10">
          <div className="flex items-start gap-4">
            <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-background text-muted-foreground">
              <SearchX aria-hidden className="size-5" />
            </span>
            <div>
              <b className="block text-2xl font-medium tracking-[-0.03em]">{m.directory_empty_title()}</b>
              <p className="mt-1 text-base text-muted-foreground">{m.directory_empty_body()}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {search.service && !search.online ? (
              <Link to="/search" search={{ service: search.service, online: true }} className={chip}>
                <Video aria-hidden className="size-4" />
                {m.directory_title_online({
                  service: SERVICE_TYPE_COPY[search.service].name(),
                })}
              </Link>
            ) : null}
            {otherCities.slice(0, 6).map((entry) => (
              <Link key={entry.city} {...sameSearchIn(search, entry.city)} className={chip}>
                <MapPin aria-hidden className="size-4" />
                {entry.city}
              </Link>
            ))}
          </div>
        </div>
      )}

      {total > cards.length ? (
        <button
          type="button"
          onClick={() => setPage((current) => current + 1)}
          disabled={next.loading}
          className={cn(chip, "mx-auto h-12 px-6 text-sm")}
        >
          {m.directory_more()}
        </button>
      ) : null}

      <nav className="mt-8 grid gap-6 border-t border-border pt-8 md:grid-cols-2">
        {search.service ? (
          <div className="grid content-start gap-3">
            <h2 className="text-sm font-medium text-muted-foreground">{m.directory_related_services()}</h2>
            <div className="flex flex-wrap gap-2">
              {relatedTypes(search.service).map((type) => {
                const Icon = GROUP_ICON[SERVICE_TYPE_GROUP[type]];
                return (
                  <Link key={type} {...sameSearchFor(search, type)} className={chip}>
                    <Icon aria-hidden className="size-4 text-muted-foreground" />
                    {SERVICE_TYPE_COPY[type].name()}
                  </Link>
                );
              })}
            </div>
          </div>
        ) : null}
        {otherCities.length > 0 ? (
          <div className="grid content-start gap-3">
            <h2 className="text-sm font-medium text-muted-foreground">{m.directory_other_cities()}</h2>
            <div className="flex flex-wrap gap-2">
              {otherCities.slice(0, 12).map((entry) => (
                <Link key={entry.city} {...sameSearchIn(search, entry.city)} className={chip}>
                  {entry.city}
                  <span className="text-muted-foreground tabular-nums">{entry.specialists}</span>
                </Link>
              ))}
            </div>
          </div>
        ) : null}
      </nav>

      <SlotGateDialog
        pick={
          pick
            ? {
                specialistId: pick.card.specialist.id,
                serviceId: pick.card.service.id,
                startAt: pick.startAt,
                organizationId: pick.card.organizationId,
              }
            : null
        }
        specialist={
          pick
            ? {
                ...pick.card.specialist,
                bio: "",
                timeZone: pick.card.business.timeZone,
                serviceIds: [pick.card.service.id],
                serviceId: pick.card.service.id,
                days: [],
                nextAvailable: null,
              }
            : null
        }
        serviceName={pick?.card.service.name ?? null}
        onClose={() => setPick(null)}
      />
    </div>
  );
}

/**
 * The same search somewhere else. A link goes straight to the address that place has, rather than
 * through a redirect, so a search engine following it spends nothing on the way.
 */
const sameSearchIn = (search: DirectorySearch, city: string) =>
  search.q
    ? { to: "/search", search: { q: search.q, city } }
    : { to: searchPagePath({ service: search.service, city }) };

/** Something else in the same place, straight to its address unless the place is the visitor's own view. */
const sameSearchFor = (search: DirectorySearch, service: ServiceType) =>
  search.online || search.near
    ? { to: "/search", search: { service, ...pickPlace(search) } }
    : { to: searchPagePath({ service, city: search.city }) };

/** Where was searched, without what: something else in the same place. */
const pickPlace = (search: DirectorySearch) =>
  search.online
    ? { online: true as const }
    : search.near
      ? { near: search.near }
      : search.city
        ? { city: search.city }
        : {};
